import {DecimalPipe} from '@angular/common';
import {Component, DestroyRef, effect, ElementRef, inject, Injectable, input, OnDestroy, OnInit, output, signal, untracked, ViewChild} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {Router} from '@angular/router';
import {Apollo} from 'apollo-angular';
import {SignalServerService} from '../../services/signal-server.service';
import {ContextService} from '../../services/context.service';
import {ProcessService} from '../../services/process.service';
import {JOIN_CRD_SESSION} from '../../queries-repository';
import {RepositoryProcessPayload} from '../../types-repository';
import {errorText} from '../../graphql/error-link';
import {environment} from '../../../environments/environment';
import {checkDrmSession, CrdDrmSession, DrmPlayback, DrmUnavailable, encryptedKinds, mediaBufferOverride, RTC_DRM_LIBRARY} from './drm-transport';
import {CRD_DRM_SESSION_SOURCE} from './drm-session';
import {CrdDisplayMode, hostPoint, pictureSize, Size, vresCommand, vresOnFollowChange} from './crd-display';
import {CrdDisplaySetting} from './crd-display-setting';

/** Paste into the host is for short text (a path, a timecode, a name); larger content goes through a workspace. */
const MAX_PASTE_BYTES = 4096;

/**
 * The local clipboard text at the last Ctrl+V or paste into a host (undefined before the first). One per host, shared
 * by its screens: they all paste into the same Windows clipboard. Provided by the page that holds the screens.
 */
@Injectable()
export class CrdLocalClipboard {
  last: string | undefined;
  /** Tells the host's other windows (a screen in a window of its own shares the same Windows clipboard). */
  onSent?: (text: string) => void;

  /** Text that just went to the host's clipboard. */
  sent(text: string) {
    this.last = text;
    this.onSent?.(text);
  }
}

/**
 * Plays a media element; if the browser blocks autoplay, retries on the next click or key press. Clear and
 * encrypted tracks alike: in an encrypted session the library feeds the element and the audio may be in the video.
 */
export function playWhenAllowed(el: HTMLMediaElement, kind: string) {
  el.play().catch((e) => {
    console.warn(`[WebRTC] ${kind} play() blocked by autoplay policy:`, e);
    const resume = () => {
      el.play().catch((e2) => console.error(`[WebRTC] ${kind} play() retry failed:`, e2));
      document.removeEventListener('click', resume);
      document.removeEventListener('keydown', resume);
    };
    document.addEventListener('click', resume, {once: true});
    document.addEventListener('keydown', resume, {once: true});
  });
}

/** What a successful joinCrdSession process carries in its `data`. */
export interface CrdJoinResult {
  signalServerUrl: string;
  session: string;
  hostPeerId: number;
  clientPeerId: number;
  password: string;
}

/**
 * One screen of a desktop host: joins the session for its monitor, then owns the WebRTC stream --
 * one RTCPeerConnection, its video/audio and the input forwarding.
 *
 * Each join starts a separate host binary process for that monitor with its own session and peer ids,
 * so several of these stream side by side. The signal server state is per stream, hence the
 * component-level SignalServerService.
 *
 * Only the `active` stream forwards keyboard, mouse and paste input; only the `audible` one plays audio. A hidden one stays
 * connected so switching back is instant.
 *
 * The picture fills a video area from the stream's top to the bottom of the window (or the screen, in full screen),
 * as the display mode says (CrdDisplayMode). In `follow` mode the host's display takes that area's size (`vres`):
 * at the channel's open, when the screen is shown, and 500 ms after resizes settle. Only a visible screen asks; a
 * hidden one keeps the size it last asked for.
 */
@Component({
  selector: 'app-crd-stream',
  templateUrl: './crd-stream.component.html',
  styleUrls: ['./crd-stream.component.scss'],
  imports: [DecimalPipe],
  providers: [SignalServerService],
})
export class CrdStreamComponent implements OnInit, OnDestroy {
  @ViewChild('remoteVideo', {static: true}) remoteVideoRef!: ElementRef<HTMLVideoElement>;
  @ViewChild('remoteAudio', {static: true}) remoteAudioRef!: ElementRef<HTMLAudioElement>;
  @ViewChild('viewerContainer', {static: true}) containerRef!: ElementRef<HTMLDivElement>;

  pool = input.required<string>();
  hostId = input.required<string>();
  hostName = input.required<string>();
  region = input.required<string>();
  monitor = input.required<number>();
  /** The visible tab: the only one that takes input. */
  active = input<boolean>(false);
  /**
   * The one stream that plays audio. Every screen's stream carries the same system audio, so with several
   * open (tabs, or windows of their own) only one may play it.
   */
  audible = input<boolean>(false);

  /** The password of the Windows user for this host session (the same for every join until the session ends). */
  joined = output<string>();

  status = signal<string>('Setting up session...');
  /** True while the join process runs, false once the stream connects (or fails to). */
  joining = signal(true);
  connected = signal(false);
  error = signal<string | null>(null);
  /** Size in bytes of the last paste, when it was too large to send to the host. */
  pasteRejected = signal<number | null>(null);
  readonly maxPasteBytes = MAX_PASTE_BYTES;

  private pc: RTCPeerConnection | null = null;
  private dataChannel: RTCDataChannel | null = null;
  private connectedToPeerId = -1;
  private isFullscreen = false;
  private lastEscapeTime = 0;
  private pingInterval: any = null;
  private statsInterval: any = null;
  private connectTimeout: any = null;
  private connectAttempts = 0;
  private destroyed = false;
  private static readonly MAX_CONNECT_ATTEMPTS = 3;
  /** This stream's monitor session (from its join), the key of its DRM session. */
  private session = '';

  // VTK-3622: the env's DRM mode decides per connection; an encrypted mode never falls back to clear.
  private readonly rtcDrmLibrary = inject(RTC_DRM_LIBRARY);
  private readonly drmSessions = inject(CRD_DRM_SESSION_SOURCE);
  private drm: DrmPlayback | null = null;
  private drmRefresh: any = null;
  /** Bumped by disconnect(): a connect() that resumes after an await of a superseded attempt stops there. */
  private connectGeneration = 0;
  private static readonly TOKEN_REFRESH_LEAD_MS = 60_000;

  // Modifier key state tracking (synced on mouse events)
  private altKeyPressed = false;
  private ctrlKeyPressed = false;
  private metaKeyPressed = false;
  private shiftKeyPressed = false;
  /** Keys forwarded down and not yet up, by keyCode: released on the host when this screen is hidden. */
  private heldKeys = new Map<number, string>();

  /** The display mode, the same for every screen in this browser. */
  private readonly display = inject(CrdDisplaySetting);
  /** The mode last acted on is `follow`. */
  private following = this.display.mode() === 'follow';
  /** The video area in CSS pixels, null while it has none (hidden, not connected) or before the first layout. */
  private area: Size | null = null;
  /** What this data channel last asked the host for; null: nothing yet, or following was turned off. */
  private vresSent: Size | null = null;
  private vresTimer: ReturnType<typeof setTimeout> | undefined;
  private layoutFrame = 0;
  /** The scheduled layout asks for the area at once (scheduleLayout(true)). */
  private layoutNow = false;
  private resizeObserver?: ResizeObserver;
  private ancestorsObserved = false;
  private static readonly VRES_DEBOUNCE_MS = 500;
  /** Below the video area, to the window's bottom edge. */
  private static readonly AREA_BOTTOM_GAP = 16;
  private static readonly AREA_MIN_HEIGHT = 240;
  private readonly onWindowResize = () => this.scheduleLayout();

  /** Every stream listens; only the one whose container went (or was) fullscreen tells its host. */
  private readonly onFullscreenChange = () => {
    const mine = document.fullscreenElement === this.containerRef.nativeElement;
    if (mine !== this.isFullscreen) {
      this.isFullscreen = mine;
      this.sendDC(mine ? 'mkon' : 'mkff');
      this.scheduleLayout();
    }
  };

  constructor(
    private router: Router,
    private apollo: Apollo,
    private processService: ProcessService,
    private contextService: ContextService,
    private signalServer: SignalServerService,
    private destroyRef: DestroyRef,
    private localClipboard: CrdLocalClipboard,
  ) {
    // Hidden streams keep playing (so switching back is instant); only the audible one is heard. The
    // visible one takes focus so keys go to it without a click.
    effect(() => {
      if (this.remoteAudioRef) this.remoteAudioRef.nativeElement.muted = !this.audible();
      // With DRM in A+V mode the library plays the decrypted audio through the video element (MSE); a clear video
      // element has no audio track, so muting it too is harmless in every mode.
      if (this.remoteVideoRef) this.remoteVideoRef.nativeElement.muted = !this.audible();
    });
    effect(() => {
      const active = this.active();
      const connected = this.connected();
      if (!this.remoteAudioRef) return;
      if (active && connected) {
        this.containerRef.nativeElement.focus();
      }
    });
    // A key held while switching tabs would otherwise stay down on the host: its key-up goes to the new tab.
    effect(() => {
      if (!this.active()) untracked(() => this.releaseHeldInput());
    });
    // Shown, or connected: the area exists now (a hidden or unconnected screen has none) and is asked for at once.
    effect(() => {
      if (this.active() && this.connected()) untracked(() => this.scheduleLayout(true));
    });
    // The display mode changed, here or in another window of this browser.
    effect(() => {
      const mode = this.display.mode();
      untracked(() => this.onDisplayMode(mode));
    });
  }

  ngOnInit() {
    document.addEventListener('fullscreenchange', this.onFullscreenChange);
    // The area's width (the page, full screen), its height (the window); its top: observeAncestors().
    this.resizeObserver = new ResizeObserver(() => this.scheduleLayout());
    this.resizeObserver.observe(this.containerRef.nativeElement);
    window.addEventListener('resize', this.onWindowResize);
    // rtc-drm-transform reports licence and decryption failures on the media elements
    for (const el of [this.remoteVideoRef.nativeElement, this.remoteAudioRef.nativeElement]) {
      el.addEventListener('rtcdrmerror', (ev: Event) => {
        if (this.drm) this.drmUnavailable(new DrmUnavailable((ev as CustomEvent).detail?.message ?? 'licence or decryption error'));
      });
    }
    this.join();
  }

  ngOnDestroy() {
    this.destroyed = true;
    document.removeEventListener('fullscreenchange', this.onFullscreenChange);
    this.resizeObserver?.disconnect();
    window.removeEventListener('resize', this.onWindowResize);
    cancelAnimationFrame(this.layoutFrame);
    this.disconnect();
  }

  /** Runs joinCrdSession for this monitor, then connects to the host binary it started. */
  join() {
    this.disconnect();
    this.error.set(null);
    this.joining.set(true);
    this.status.set('Setting up session...');

    this.apollo.mutate({
      mutation: JOIN_CRD_SESSION,
      variables: {input: {pool: this.pool(), hostInstance: this.hostId(), region: this.region(), monitor: this.monitor()}},
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (result) => {
        const process = (result.data as any)?.joinCrdSession as RepositoryProcessPayload;
        if (!process?.id) {
          this.error.set('Failed to start join process');
          return;
        }
        this.processService.observeProcess(process, {
          failureMessage: `Failed to join session on ${this.hostName()}`,
        }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
          next: (p) => {
            if (p.state === 'SUCCESS' && p.data) {
              // The process data may arrive JSON-encoded more than once.
              let parsed: any = p.data;
              while (typeof parsed === 'string') parsed = JSON.parse(parsed);
              this.onJoined(parsed as CrdJoinResult);
            } else if (p.state === 'FAILED') {
              this.error.set(p.message || 'Unknown error');
            } else if (p.state === 'IN_PROGRESS') {
              this.status.set('Setting up session...');
            }
          },
        });
      },
      error: (err) => {
        this.error.set(`Failed to join session: ${err?.message || 'Unknown error'}`);
      },
    });
  }

  private onJoined(result: CrdJoinResult) {
    // The process subject replays its last state; a stream joins once per attempt.
    if (!this.joining() || this.destroyed) return;
    this.joining.set(false);
    this.joined.emit(result.password);
    this.session = result.session;
    this.signalServer.configure(result.signalServerUrl, result.session, Number(result.clientPeerId));
    this.connectAttempts = 0;
    this.connect(Number(result.hostPeerId));
  }

  private async connect(hostPeerId: number | null) {
    const generation = this.connectGeneration;
    const superseded = () => generation !== this.connectGeneration || this.destroyed;
    this.error.set(null);
    try {
      if (!hostPeerId) {
        this.error.set('Host is not registered on signal server. Try Bootstrap Host first.');
        return;
      }

      this.connectedToPeerId = hostPeerId;
      this.status.set(`Connecting to ${this.hostName()}...`);

      // Every (re)connect fetches a fresh DRM session before the peer connection exists.
      const session = await this.fetchDrmSession();
      if (!session || superseded()) return;
      const encrypted = encryptedKinds(session.mode).size > 0;

      this.pc = new RTCPeerConnection({
        iceServers: [
          {urls: ['stun:stun.l.google.com:19302']},
          {urls: ['stun:stun2.l.google.com:19302']},
        ],
        // rtc-drm-transform works on the encoded frames
        ...(encrypted ? {encodedInsertableStreams: true} : {}),
      } as RTCConfiguration);

      this.drm = null;
      if (encrypted) {
        try {
          const bufferOverride = mediaBufferOverride(environment.production, globalThis.localStorage ?? null);
          if (bufferOverride !== null) console.info(`[DRM] mediaBufferMs ${bufferOverride} from localStorage (test override)`);
          const library = await this.rtcDrmLibrary();
          if (superseded()) {
            this.pc?.close();
            this.pc = null;
            return;
          }
          this.drm = new DrmPlayback(library, session,
            this.remoteVideoRef.nativeElement, this.remoteAudioRef.nativeElement, bufferOverride);
        } catch (e) {
          this.drmUnavailable(e);
          return;
        }
        this.scheduleTokenRefresh(session);
      }

      this.pc.onicecandidate = (event) => {
        if (event.candidate) {
          console.log(`[WebRTC] ICE candidate: ${event.candidate.type} ${event.candidate.protocol} ${event.candidate.address}:${event.candidate.port}`);
          this.signalServer.sendMessage(this.connectedToPeerId, JSON.stringify(event.candidate));
        } else {
          console.log('[WebRTC] ICE gathering complete');
        }
      };

      this.pc.onicecandidateerror = (event) => {
        console.warn(`[WebRTC] ICE candidate error: ${event.errorCode} ${event.errorText} (${event.url})`);
      };

      this.pc.oniceconnectionstatechange = () => {
        console.log(`[WebRTC] ICE connection state: ${this.pc?.iceConnectionState}`);
      };

      this.pc.onicegatheringstatechange = () => {
        console.log(`[WebRTC] ICE gathering state: ${this.pc?.iceGatheringState}`);
      };

      // Retry: if not connected after 15s, tear down and rebuild the offer (max 3 attempts)
      clearTimeout(this.connectTimeout);
      this.connectTimeout = setTimeout(() => {
        if (!this.connected()) {
          const iceState = this.pc?.iceConnectionState;
          const connState = this.pc?.connectionState;
          console.log(`[WebRTC] Timeout — iceState=${iceState} connState=${connState}`);
          this.connectAttempts++;
          if (this.connectAttempts >= CrdStreamComponent.MAX_CONNECT_ATTEMPTS) {
            console.log('[WebRTC] Max connection attempts reached, giving up');
            const hint = iceState === 'checking'
              ? 'ICE negotiation timed out — the host may be unreachable (check security group UDP rules or add a TURN server).'
              : iceState === 'failed'
                ? 'ICE failed — no viable network path found between browser and host.'
                : 'No response from host binary — it may have crashed or cannot reach the signal server.';
            this.error.set(`Could not connect to ${this.hostName()}. ${hint}`);
            this.pc?.close();
            this.pc = null;
            return;
          }
          console.log(`[WebRTC] Connection timeout, retrying (attempt ${this.connectAttempts + 1}/${CrdStreamComponent.MAX_CONNECT_ATTEMPTS})...`);
          this.status.set(`Retrying connection (${this.connectAttempts + 1}/${CrdStreamComponent.MAX_CONNECT_ATTEMPTS})...`);
          this.pc?.close();
          this.pc = null;
          this.connect(hostPeerId);
        }
      }, 15000);

      this.pc.onconnectionstatechange = () => {
        const state = this.pc?.connectionState;
        console.log(`[WebRTC] Connection state: ${state}`);
        if (state === 'connected') {
          clearTimeout(this.connectTimeout);
          this.status.set(`Connected to ${this.hostName()}`);
          this.connected.set(true);
          this.error.set(null);
          if (this.active()) {
            this.containerRef.nativeElement.focus();
          }
          this.sendDC('mkon');
          // Log stats periodically
          this.startStatsLogging();
        } else if (state === 'disconnected' || state === 'failed') {
          clearTimeout(this.connectTimeout);
          console.log(`[WebRTC] Connection lost — state: ${state}`);
          this.status.set('Disconnected');
          this.connected.set(false);
          this.error.set('Connection lost');
        }
      };

      this.pc.addTransceiver('video', {direction: 'recvonly'});
      this.pc.addTransceiver('audio', {direction: 'recvonly'});

      this.pc.ontrack = (event) => {
        console.log(`[WebRTC] ontrack: kind=${event.track.kind} id=${event.track.id}`);
        if (this.drm) {
          // Encrypted session: the library takes every track -- it decrypts the encrypted ones via MSE/EME and
          // plays the clear one itself, in sync with the video. The raw track is never attached here.
          try {
            this.drm.onTrack(event);
          } catch (e) {
            this.drmUnavailable(e);
            return;
          }
          const el = event.track.kind === 'video' ? this.remoteVideoRef.nativeElement : this.remoteAudioRef.nativeElement;
          playWhenAllowed(el, event.track.kind);
          return;
        }
        if (event.track.kind === 'video') {
          const stream = new MediaStream([event.track]);
          const video = this.remoteVideoRef.nativeElement;
          video.srcObject = stream;
          video.play().catch((e) => console.warn('[WebRTC] video play() rejected:', e));
        } else if (event.track.kind === 'audio') {
          const audio = this.remoteAudioRef.nativeElement;
          audio.srcObject = new MediaStream([event.track]);
          playWhenAllowed(audio, 'audio');
        }
      };

      this.dataChannel = this.pc.createDataChannel('mk', {ordered: true});
      // A new channel is a new viewer to the host binary: it has been asked for nothing yet.
      this.vresSent = null;
      this.dataChannel.onopen = () => {
        this.scheduleLayout(true);
        this.pingInterval = setInterval(() => {
          if (this.dataChannel?.readyState === 'open') {
            this.dataChannel.send('ping ' + Date.now());
          }
        }, 5000);
      };
      this.dataChannel.onclose = () => {
        if (this.pingInterval) clearInterval(this.pingInterval);
      };
      this.dataChannel.onmessage = (event) => this.onHostMessage(event.data as string);

      this.pc.onnegotiationneeded = async () => {
        try {
          const offer = await this.pc!.createOffer();
          await this.pc!.setLocalDescription(offer);
          const ld = this.pc!.localDescription!;
          this.signalServer.sendMessage(this.connectedToPeerId, JSON.stringify({sdp: ld.sdp, type: ld.type}));
        } catch (e) {
          console.error('Negotiation failed:', e);
        }
      };

      this.signalServer.startPolling(async (data) => {
        if (!this.pc) return;
        if (data === 'BYE') { this.disconnect(); return; }

        try {
          const msg = JSON.parse(data);
          if (msg.type === 'offer' || msg.type === 'answer') {
            await this.pc.setRemoteDescription(new RTCSessionDescription(msg));
            if (msg.type === 'offer') {
              const answer = await this.pc.createAnswer();
              await this.pc.setLocalDescription(answer);
              this.signalServer.sendMessage(this.connectedToPeerId, JSON.stringify(this.pc.localDescription));
            }
          } else if (msg.candidate !== undefined) {
            await this.pc.addIceCandidate(new RTCIceCandidate(msg));
          }
        } catch (e) {
          console.error('[WebRTC] Error processing signal message:', e);
        }
      });
    } catch (e: any) {
      this.error.set(`Connection failed: ${e.message}`);
    }
  }

  private startStatsLogging() {
    if (this.statsInterval) clearInterval(this.statsInterval);
    this.statsInterval = setInterval(async () => {
      if (!this.pc) return;
      try {
        const audio = this.remoteAudioRef.nativeElement;
        console.log(`[Audio] screen ${this.monitor() + 1} element: paused=${audio.paused} muted=${audio.muted} volume=${audio.volume}`);
        const audioTrack = (audio.srcObject as MediaStream)?.getAudioTracks()[0];
        if (audioTrack) {
          console.log(`[Audio] track: enabled=${audioTrack.enabled} muted=${audioTrack.muted} state=${audioTrack.readyState}`);
        }

        const stats = await this.pc.getStats();
        stats.forEach((report: any) => {
          if (report.type === 'candidate-pair' && report.state === 'succeeded') {
            console.log(`[WebRTC] Stats: rtt=${report.currentRoundTripTime}s bytes=${report.bytesReceived} nominated=${report.nominated}`);
          }
          if (report.type === 'inbound-rtp' && report.kind === 'audio') {
            console.log(`[Audio] inbound-rtp: bytesReceived=${report.bytesReceived} audioLevel=${report.audioLevel?.toFixed(4)} totalAudioEnergy=${report.totalAudioEnergy?.toFixed(4)} packetsLost=${report.packetsLost}`);
          }
        });
      } catch { /* ignore */ }
    }, 5000);
  }

  /** A message from the host binary on the data channel. */
  onHostMessage(msg: string) {
    if (msg.startsWith('moim')) {
      const video = this.remoteVideoRef.nativeElement;
      const text = msg.substring(5);
      if (text) {
        const parts = text.split(' ');
        const kscale = video.videoWidth / video.offsetWidth;
        const xshift = parseInt(parts[0]) / kscale;
        const yshift = parseInt(parts[1]) / kscale;
        const base64 = parts[2];
        video.style.cursor = `-webkit-image-set(url(${base64}) ${kscale}x) ${xshift} ${yshift}, auto`;
      } else {
        video.style.cursor = 'none';
      }
    }
    // The host's clipboard ('clpa <text>') is ignored: the clipboard goes only towards the host. Not a boundary —
    // the binary still sends it and the editor controls this page — but nothing is copied off the host by accident.
  }

  /** The DRM session for this (re)connect, checked for its mode; null (with the error shown) if unusable. */
  private async fetchDrmSession(): Promise<CrdDrmSession | null> {
    let session: CrdDrmSession;
    try {
      session = await this.drmSessions({pool: this.pool(), hostInstance: this.hostId(), session: this.session});
      checkDrmSession(session);
    } catch (e) {
      this.drmUnavailable(e);
      return null;
    }
    return session;
  }

  /** Ends the attempt with "DRM unavailable: <reason>"; an encrypted session is never played in the clear. */
  private drmUnavailable(e: unknown) {
    const reason = e instanceof DrmUnavailable ? e.message : errorText(e);
    console.error('[DRM] unavailable:', e);
    this.disconnect();
    this.status.set('Disconnected');
    this.error.set(`DRM unavailable: ${reason}`);
  }

  /** Fetches a fresh token shortly before the current one expires, for the licence requests still to come. */
  private scheduleTokenRefresh(session: CrdDrmSession) {
    if (this.drmRefresh) clearTimeout(this.drmRefresh);
    if (!session.tokenExpiresAt) return;
    const delay = Date.parse(session.tokenExpiresAt) - Date.now() - CrdStreamComponent.TOKEN_REFRESH_LEAD_MS;
    // A refresh belongs to its connection: once that is superseded (disconnect, retry) neither its new token nor its
    // failure may touch the connection that replaced it.
    const generation = this.connectGeneration;
    const current = () => generation === this.connectGeneration && !this.destroyed && !!this.drm;
    this.drmRefresh = setTimeout(async () => {
      let fresh: CrdDrmSession;
      try {
        fresh = await this.drmSessions({pool: this.pool(), hostInstance: this.hostId(), session: this.session});
        checkDrmSession(fresh);
      } catch (e) {
        if (current()) this.drmUnavailable(e);
        return;
      }
      if (!current()) return;
      this.drm!.updateToken(fresh.token!);
      this.scheduleTokenRefresh(fresh);
    }, Math.max(delay, 0));
  }

  disconnect() {
    this.connectGeneration++;
    clearTimeout(this.vresTimer);
    this.vresSent = null;
    if (this.drmRefresh) clearTimeout(this.drmRefresh);
    this.drmRefresh = null;
    this.drm?.dispose();
    this.drm = null;
    clearTimeout(this.connectTimeout);
    if (this.pingInterval) clearInterval(this.pingInterval);
    if (this.statsInterval) clearInterval(this.statsInterval);
    this.signalServer.stopPolling();
    if (this.connectedToPeerId > 0) {
      this.signalServer.sendMessage(this.connectedToPeerId, 'BYE');
    }
    this.dataChannel?.close();
    this.dataChannel = null;
    this.pc?.close();
    this.pc = null;
    this.connected.set(false);
  }

  /** Try Again: a failed join joins again; a failed or lost connection reconnects to the same host binary. */
  retry() {
    if (this.joining()) {
      this.join();
      return;
    }
    this.disconnect();
    this.error.set(null);
    this.connectAttempts = 0;
    this.connect(this.connectedToPeerId);
  }

  goBack() {
    this.router.navigate(['/o', this.contextService.organization()?.id, 'crd']);
  }

  toggleFullscreen() {
    if (!document.fullscreenElement) {
      this.containerRef.nativeElement.requestFullscreen?.();
    } else {
      document.exitFullscreen?.();
    }
  }

  onPaste(event: ClipboardEvent) {
    if (!this.active()) return;
    const text = event.clipboardData?.getData('text');
    if (text) {
      event.preventDefault();
      if (this.sendClipboard(text)) this.localClipboard.sent(text);
    }
  }

  /** Sends text to the host's clipboard, unless it is over the limit. False when it was not sent. */
  private sendClipboard(text: string): boolean {
    const bytes = new TextEncoder().encode(text).length;
    if (bytes > MAX_PASTE_BYTES) {
      this.pasteRejected.set(bytes);
      return false;
    }
    this.pasteRejected.set(null);
    this.sendDC('clpa ' + text);
    return true;
  }

  // --- Input forwarding using the old string-based protocol ---

  private sendDC(msg: string) {
    if (this.dataChannel?.readyState === 'open') {
      this.dataChannel.send(msg);
    }
  }

  /** Input goes to the host only from the visible screen with an open data channel. */
  private takesInput(): boolean {
    return this.active() && this.dataChannel?.readyState === 'open';
  }

  /** A mouse event → host pixel. The video element is exactly the picture (layoutVideo), so no letterbox to skip. */
  private getHostCoords(event: MouseEvent): {x: number, y: number} {
    const video = this.remoteVideoRef.nativeElement;
    const rect = video.getBoundingClientRect();
    return hostPoint(event.clientX - rect.left, event.clientY - rect.top,
      {w: video.videoWidth, h: video.videoHeight}, {w: rect.width, h: rect.height});
  }

  // --- The video area, the picture in it, and the host's display following it (vres) ---

  /** Lays out on the next frame, once for any number of triggers; `now`: ask for the area without the debounce. */
  private scheduleLayout(now = false) {
    this.layoutNow ||= now;
    if (this.layoutFrame) return;
    this.layoutFrame = requestAnimationFrame(() => {
      this.layoutFrame = 0;
      const immediate = this.layoutNow;
      this.layoutNow = false;
      this.layout(immediate);
    });
  }

  /** Measures the video area, fits the picture into it and, when following, asks the host for it. */
  layout(now = false) {
    this.observeAncestors();
    this.area = this.measureArea();
    this.layoutVideo();
    if (now) this.sendArea('shown');
    else this.scheduleArea();
  }

  /**
   * Content shown or hidden above the stream (the password, an alert) moves the area's top without resizing the area
   * (its height is set) or the body (100 % of the window). It resizes an ancestor, so they are all observed. Once the
   * stream is in the page: at its first layout.
   */
  private observeAncestors() {
    if (this.ancestorsObserved || !this.containerRef.nativeElement.isConnected) return;
    this.ancestorsObserved = true;
    for (let el = this.containerRef.nativeElement.parentElement; el && el !== document.documentElement; el = el.parentElement) {
      this.resizeObserver?.observe(el);
    }
  }

  /**
   * The video area: the page's width (the container's) and the window's height below the stream's top; in full screen
   * the screen. null while it has no size (a hidden tab, not connected yet).
   */
  private measureArea(): Size | null {
    const wrap = this.containerRef.nativeElement;
    if (document.fullscreenElement === wrap) {
      wrap.style.height = '';
    } else {
      const top = wrap.getBoundingClientRect().top + window.scrollY;
      const height = Math.floor(window.innerHeight - top - CrdStreamComponent.AREA_BOTTOM_GAP);
      wrap.style.height = `${Math.max(height, CrdStreamComponent.AREA_MIN_HEIGHT)}px`;
    }
    const w = wrap.clientWidth, h = wrap.clientHeight;
    return w > 0 && h > 0 ? {w, h} : null;
  }

  /** Sizes the video element to the picture, as the display mode says. Also on every new stream size. */
  layoutVideo() {
    const video = this.remoteVideoRef.nativeElement;
    const shown = this.area && pictureSize(this.display.mode(), {w: video.videoWidth, h: video.videoHeight}, this.area);
    video.style.width = shown ? `${shown.w}px` : '';
    video.style.height = shown ? `${shown.h}px` : '';
  }

  /** The area as the host may see it: only a visible screen asks for its size. */
  private visibleArea(): Size | null {
    return this.active() ? this.area : null;
  }

  private channelOpen(): boolean {
    return this.dataChannel?.readyState === 'open';
  }

  /** Asks for the area once resizes have settled. */
  private scheduleArea() {
    if (!this.following) return;
    clearTimeout(this.vresTimer);
    this.vresTimer = setTimeout(() => this.sendArea('resized'), CrdStreamComponent.VRES_DEBOUNCE_MS);
  }

  /** Asks the host for the area now, unless not following, hidden, closed, or that size was asked for already. */
  private sendArea(reason: string) {
    clearTimeout(this.vresTimer);
    if (!this.channelOpen()) return;
    const area = this.visibleArea();
    const cmd = vresCommand(this.following, area, this.vresSent);
    if (!cmd) return;
    this.vresSent = {w: area!.w, h: area!.h};
    this.sendVres(cmd, reason);
  }

  private onDisplayMode(mode: CrdDisplayMode) {
    const on = mode === 'follow';
    clearTimeout(this.vresTimer);
    const r = vresOnFollowChange(this.following, on, this.visibleArea(), this.vresSent, this.channelOpen());
    this.following = on;
    this.vresSent = r.sent;
    for (const cmd of r.commands) this.sendVres(cmd, `display mode ${mode}`);
    this.layoutVideo();
  }

  private sendVres(cmd: string, reason: string) {
    console.log(`[Display] screen ${this.monitor() + 1}: ${cmd} (${reason})`);
    this.sendDC(cmd);
  }

  /** Sync modifier key state — the OS may intercept key events */
  private syncModifiers(event: MouseEvent) {
    if (event.altKey !== this.altKeyPressed) {
      this.sendDC(event.altKey ? 'kewn 18' : 'keup 18');
      this.altKeyPressed = event.altKey;
    }
    if (event.ctrlKey !== this.ctrlKeyPressed) {
      this.sendDC(event.ctrlKey ? 'kewn 17' : 'keup 17');
      this.ctrlKeyPressed = event.ctrlKey;
    }
    if (event.metaKey !== this.metaKeyPressed) {
      this.sendDC(event.metaKey ? 'kewn 91' : 'keup 91');
      this.metaKeyPressed = event.metaKey;
    }
    if (event.shiftKey !== this.shiftKeyPressed) {
      this.sendDC(event.shiftKey ? 'kewn 16' : 'keup 16');
      this.shiftKeyPressed = event.shiftKey;
    }
  }

  onMouseEvent(event: MouseEvent) {
    if (!this.takesInput()) return;
    const {x, y} = this.getHostCoords(event);
    this.syncModifiers(event);

    // Command format: first 2 chars + last 2 chars of event type
    // mousemove → "move", mouseup → "moup", mousedown → "mown"
    const type = event.type.slice(0, 2) + event.type.slice(-2);
    this.sendDC(`${type} ${x} ${y} ${event.buttons}`);
    event.preventDefault();
  }

  onKeyEvent(event: KeyboardEvent) {
    if (!this.takesInput()) return;
    // Double-Escape exits fullscreen (single Escape is forwarded to Windows)
    if (event.key === 'Escape' && this.isFullscreen && event.type === 'keydown') {
      const now = Date.now();
      if (now - this.lastEscapeTime < 500) {
        document.exitFullscreen?.();
        this.lastEscapeTime = 0;
        return;
      }
      this.lastEscapeTime = now;
    }
    // On Ctrl+V: read browser clipboard, sync to remote, then forward the keystroke.
    // (preventDefault on keydown suppresses the paste event, so onPaste() never fires for Ctrl+V.)
    if (event.type === 'keydown' && event.keyCode === 86 && event.ctrlKey && navigator.clipboard?.readText) {
      event.preventDefault();
      event.stopPropagation();
      navigator.clipboard.readText().then(text => {
        // The tab may have been switched while the read (or its permission prompt) was pending: then nothing goes,
        // or V would stay down on a host whose keys were already released.
        if (!this.active()) return;
        // Only a local clipboard that changed since the last Ctrl+V goes to the host. Otherwise the host pastes
        // its own: the host's clipboard never comes back here, so resending the same local text would overwrite
        // whatever was copied inside Windows since.
        // The first Ctrl+V of a page has nothing to compare with, so it always sends the local clipboard. If the
        // editor had copied something inside Windows, that copy is overwritten, and Ctrl+V again pastes the same
        // local text: they have to copy it again inside Windows. That is an acceptable cost (one wrong paste per
        // page, nothing leaves the host) for keeping Ctrl+V the one paste gesture, rather than adding a separate
        // "paste from this computer" action.
        if (text && text !== this.localClipboard.last) {
          // Too large: no keystroke either, or the host would paste what its own clipboard held.
          if (!this.sendClipboard(text)) return;
          this.localClipboard.sent(text);
        }
        this.sendKey('kewn', 86, 'v');
      }).catch(() => {
        if (this.active()) this.sendKey('kewn', 86, 'v'); // forward keystroke even if clipboard read is denied
      });
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    // keyCode is deprecated but the host binary expects it
    this.sendKey(event.type === 'keydown' ? 'kewn' : 'keup', event.keyCode, event.key);
  }

  private sendKey(type: 'kewn' | 'keup', keyCode: number, key: string) {
    if (type === 'kewn') this.heldKeys.set(keyCode, key);
    else this.heldKeys.delete(keyCode);
    this.sendDC(`${type} ${keyCode} ${key}`);
  }

  /** Key-ups for every key and modifier this screen sent down, so nothing stays pressed on its host. */
  private releaseHeldInput() {
    for (const [keyCode, key] of this.heldKeys) this.sendDC(`keup ${keyCode} ${key}`);
    this.heldKeys.clear();
    const modifiers: [boolean, number][] = [
      [this.altKeyPressed, 18], [this.ctrlKeyPressed, 17], [this.metaKeyPressed, 91], [this.shiftKeyPressed, 16]];
    for (const [pressed, keyCode] of modifiers) if (pressed) this.sendDC(`keup ${keyCode}`);
    this.altKeyPressed = this.ctrlKeyPressed = this.metaKeyPressed = this.shiftKeyPressed = false;
  }

  onWheel(event: WheelEvent) {
    if (!this.takesInput()) return;
    event.preventDefault();
    this.sendDC(`whel ${Math.round(event.deltaX)} ${Math.round(event.deltaY)}`);
  }
}
